// 🟢 Mise en ligne du prof. À chaque connexion ou reconnexion (connexion au compte, page
// ouverte ou rechargée, retour après une absence qui l'a mis hors ligne), le prof est hors
// ligne et le site lui propose « Vous êtes hors ligne. Passer en ligne ? ». Le passage en ligne
// est vérifié par l'API (téléphone vérifié, IBAN enregistré, compte Stripe prêt) : s'il manque
// quelque chose, la liste s'affiche avec les liens pour le régler.

// ---------- Fenêtre « Passer en ligne ? » : demandée de n'importe où (connexion, menu…),
// affichée par TeacherOnlinePrompt, même si elle n'est pas encore montée
const listeners = new Set();
let pending = null;

// detail : { missing } pour afficher directement ce qui manque
export const requestOnlinePrompt = (detail = {}) => {
  if (listeners.size) listeners.forEach((listener) => listener(detail));
  else pending = detail;
};

export const onOnlinePrompt = (listener) => {
  listeners.add(listener);
  if (pending) {
    const detail = pending;
    pending = null;
    listener(detail);
  }
  return () => listeners.delete(listener);
};

// Demande d'élève acceptée (tour 65, A11) : la fenêtre « Passer en ligne ? » se ferme. Ouverte au rechargement
// de la page (le prof y est remis hors ligne) avec la fenêtre de la demande, elle restait par-dessus le cours
const acceptedListeners = new Set();
export const notifyCourseAccepted = () => acceptedListeners.forEach((listener) => listener());
export const onCourseAccepted = (listener) => {
  acceptedListeners.add(listener);
  return () => acceptedListeners.delete(listener);
};

// Hors ligne demandé par le prof lui-même (menu) : pas de fenêtre « Passer en ligne ? »
let offlineByTeacher = false;
export const markOfflineByTeacher = () => (offlineByTeacher = true);
export const consumeOfflineByTeacher = () => {
  const value = offlineByTeacher;
  offlineByTeacher = false;
  return value;
};

// Demande d'élève restée sans réponse (tour 62, B4) : la fenêtre « Passer en ligne ? » dit pourquoi le prof est hors
// ligne. Notée au refus automatique (fenêtre de la demande, Alert.js, avant de l'envoyer à l'API ; tâche du serveur,
// événement « invitation-auto-rejected », App.js), lue au passage hors ligne qui suit. Valable une minute
const MISSED_REQUEST_MS = 60 * 1000;
let missedRequestAt = 0;
export const markMissedRequest = (now = Date.now()) => (missedRequestAt = now);
export const consumeMissedRequest = (now = Date.now()) => {
  const recent = missedRequestAt > 0 && now - missedRequestAt <= MISSED_REQUEST_MS;
  missedRequestAt = 0;
  return recent;
};

// ---------- Ce qui manque (réponse 409 de l'API), avec le lien pour le régler
export const missingItemsOf = (missing = []) =>
  missing.map((item) => {
    if (item.key === "phone") return { key: "phone", text: "teacherOnline.missing.phone", link: "/myAccount", linkText: "teacherOnline.links.myAccount" };
    if (item.key === "iban") return { key: "iban", text: "teacherOnline.missing.iban", link: "/MyPayments?iban=1", linkText: "teacherOnline.links.myPayments" };
    if (item.reason === "no_account")
      return { key: "stripe", text: "teacherOnline.missing.stripeNoAccount", link: "/MyPayments?iban=1", linkText: "teacherOnline.links.myPayments" };
    if (item.reason === "stripe_error") return { key: "stripe", text: "teacherOnline.missing.stripeError" };
    if (item.reason === "account_not_found") return { key: "stripe", text: "teacherOnline.missing.stripeNotFound", link: "/contact", linkText: "teacherOnline.links.contact" };
    if (item.reason === "requirements_due") return { key: "stripe", text: "teacherOnline.missing.stripeRequirements", stripeLink: true };
    // Désactivé par Stripe sans rien à fournir (fraude, examen) : rien à compléter, le support
    if (item.reason === "account_under_review") return { key: "stripe", text: "teacherOnline.missing.stripeUnderReview", link: "/contact", linkText: "teacherOnline.links.contact" };
    return { key: "stripe", text: "teacherOnline.missing.stripePending", stripeLink: true }; // vérification en cours chez Stripe
  });

// ---------- 💻 Au téléphone, conseil de l'ordinateur avant chaque passage en ligne (tour 49),
// affiché par ComputerHint (monté dans App) : listener(resolve), resolve(true) pour passer en
// ligne. Sans fenêtre montée ou si elle échoue, le prof passe en ligne quand même.
let computerHint = null;
export const onComputerHint = (listener) => {
  computerHint = listener;
  return () => {
    if (computerHint === listener) computerHint = null;
  };
};
const confirmOnline = () =>
  new Promise((resolve) => {
    if (!computerHint) return resolve(true);
    try {
      computerHint(resolve);
    } catch (err) {
      resolve(true);
    }
  });

// ---------- Passage en ligne : { online: true }, { missing: [...] } ou { cancelled: true } (« Annuler »
// dans le conseil de l'ordinateur, tour 49). Autre erreur : levée
export const goOnline = async (teacherApi, userId) => {
  if (!(await confirmOnline())) return { cancelled: true };
  try {
    await teacherApi.updateStatus(userId, 1);
    return { online: true };
  } catch (err) {
    const data = err?.response?.data;
    if (err?.response?.status === 409 && Array.isArray(data?.missing)) return { missing: data.missing };
    throw err;
  }
};

// Type de compte lu dans le jeton (lecture seule : c'est l'API qui vérifie)
export const tokenUserType = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload))?.userType ?? null;
  } catch (err) {
    return null;
  }
};
